import axios from "axios";
import React from "react";
import { useNavigate } from "react-router-dom";

const CategorySlidePanelForm = ({
  type,
  isOpen,
  onClose,
  onSave,
  buttonColor,
  title = "Category Settings",
  config,
  setConfig,
  onChange,
  addContentUrl,
  userId,
}) => {
  const [mainCategoryConfig, setMainCategoryConfig] = React.useState(
    config || {
      numColumns: 1,
      columnSpacing: 10,
      buttonGap: 10,
      enableOffset: true,
      leftOffset: 5,
      rightOffset: 0,
      topOffset: 5,
      disableAnimation: false,
      buttonAlignment: "center",
      buttonMarginTop: 0,
      buttonMarginBottom: 0,
    },
  );

  const [subCategoryConfig, setSubCategoryConfig] = React.useState({
    numColumns: "center-two-buttons-per-row",
    columnSpacing: 5,
    buttonGap: 10,
    enableOffset: false,
    leftOffset: 5,
    rightOffset: 0,
    topOffset: 5,
    disableAnimation: false,
    buttonAlignment: "center",
    buttonMarginTop: 0,
    buttonMarginBottom: 0,
  });

  const navigate = useNavigate();

  // Update local config when panel opens
  React.useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchBothSettings = async () => {
      try {
        // Fetch Main Category settings
        const mainRes = await axios.get(
          `/api/wayfinder/wayfinder-frontend-main-menu-settings/${userId}`,
        );
        if (mainRes.data?.success && mainRes.data?.data) {
          setMainCategoryConfig(mainRes.data.data);
          if (setConfig) setConfig(mainRes.data.data);
        }

        // Fetch SubCategory settings
        const subRes = await axios.get(
          `/api/wayfinder/frontend-main-subcategory-settings/${userId}`,
        );
        if (subRes.data?.success && subRes.data?.data) {
          setSubCategoryConfig(subRes.data.data);
        }
      } catch (error) {
        console.error("Failed to load wayfinder settings", error);
      }
    };

    fetchBothSettings();
  }, [isOpen, userId]);

  const handleMainCategoryChange = (field, value) => {
    const newConfig = { ...mainCategoryConfig, [field]: value };
    setMainCategoryConfig(newConfig);
  };

  const handleSubCategoryChange = (field, value) => {
    const newConfig = { ...subCategoryConfig, [field]: value };
    setSubCategoryConfig(newConfig);
  };

  const handleSave = async () => {
    try {
      // Save both main and subcategory settings
      await Promise.all([
        axios.post(
          `/api/wayfinder/frontend-main-category-settings/${userId}`,
          mainCategoryConfig,
        ),
        axios.post(
          `/api/wayfinder/frontend-main-subcategory-settings/${userId}`,
          subCategoryConfig,
        ),
      ]);
      onSave(mainCategoryConfig);
      onClose();
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  return (
    <div className={`side-panel ${isOpen ? "open" : ""}`}>
      <div className="side-panel-content">
        <h4 style={{ textAlign: "center" }}>{title}</h4>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "30px",
            marginTop: "31px",
          }}
        >
          {/* MAIN CATEGORY SETTINGS COLUMN */}
          <div>
            <h5
              style={{
                textAlign: "center",
                fontWeight: "bold",
                marginBottom: "20px",
                paddingBottom: "10px",
                borderBottom: `2px solid ${buttonColor}`,
              }}
            >
              Main Category
            </h5>

            {/* Number of Columns */}
            <div className="row">
              <div className="col-md-12">
                <span style={{ fontWeight: "normal" }}>
                  Number of Button Columns:
                </span>
                <div className="setting form-group">
                  <select
                    value={mainCategoryConfig.numColumns}
                    onChange={(e) =>
                      handleMainCategoryChange("numColumns", e.target.value)
                    }
                    className="form-control"
                  >
                    <option value={""}>Select Button Columns</option>
                    <option value={"center-single-row"}>
                      Center Single Row
                    </option>
                    <option value={"center-two-buttons-per-row"}>
                      Center 2 Buttons Per Row
                    </option>
                    <option value={"left-side-one-button-per-row"}>
                      Left Side One Button Per Row
                    </option>
                    <option value={"right-side-one-button-per-row"}>
                      Right Side One Button Per Row
                    </option>
                    <option value={"unlimited-buttons-center"}>
                      Unlimited Buttons Center
                    </option>
                    <option value={"unlimited-left-buttons-center"}>
                      Unlimited Left Buttons Center
                    </option>
                    <option value={"unlimited-right-buttons-center"}>
                      Unlimited Right Buttons Center
                    </option>
                  </select>
                </div>
              </div>
            </div>

            {/* Column Spacing */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Column Spacing In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={mainCategoryConfig.columnSpacing}
                onChange={(e) =>
                  handleMainCategoryChange("columnSpacing", Number(e.target.value))
                }
              />
            </div>

            {/* Button Gap */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>Button Gap In px:</label>
              <input
                type="number"
                className="form-control"
                value={mainCategoryConfig.buttonGap || 10}
                onChange={(e) =>
                  handleMainCategoryChange("buttonGap", Number(e.target.value))
                }
              />
            </div>

            {/* Button Margin Top */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Margin Top In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={mainCategoryConfig.buttonMarginTop || 0}
                onChange={(e) =>
                  handleMainCategoryChange(
                    "buttonMarginTop",
                    Number(e.target.value),
                  )
                }
              />
            </div>

            {/* Button Margin Bottom */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Margin Bottom In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={mainCategoryConfig.buttonMarginBottom || 0}
                onChange={(e) =>
                  handleMainCategoryChange(
                    "buttonMarginBottom",
                    Number(e.target.value),
                  )
                }
              />
            </div>

            {/* Disable Animation */}
            <div className="setting form-group custom-control custom-checkbox">
              <input
                type="checkbox"
                checked={mainCategoryConfig.disableAnimation}
                onChange={(e) =>
                  handleMainCategoryChange("disableAnimation", e.target.checked)
                }
                id="disable_animation_main"
                className="custom-control-input"
              />
              <label
                className="custom-control-label"
                htmlFor="disable_animation_main"
              >
                Disable Button Animation
              </label>
            </div>

            {/* Enable Offset */}
            <div className="setting form-group custom-control custom-checkbox">
              <input
                type="checkbox"
                checked={mainCategoryConfig.enableOffset}
                onChange={(e) =>
                  handleMainCategoryChange("enableOffset", e.target.checked)
                }
                id="enable_offset_main"
                className="custom-control-input"
              />
              <label className="custom-control-label" htmlFor="enable_offset_main">
                Enable Offset
              </label>
            </div>

            {mainCategoryConfig.enableOffset && (
              <>
                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Left Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={mainCategoryConfig.leftOffset}
                    onChange={(e) =>
                      handleMainCategoryChange("leftOffset", Number(e.target.value))
                    }
                  />
                </div>

                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Right Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={mainCategoryConfig.rightOffset}
                    onChange={(e) =>
                      handleMainCategoryChange(
                        "rightOffset",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>

                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Top Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={mainCategoryConfig.topOffset}
                    onChange={(e) =>
                      handleMainCategoryChange("topOffset", Number(e.target.value))
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* SUBCATEGORY SETTINGS COLUMN */}
          <div>
            <h5
              style={{
                textAlign: "center",
                fontWeight: "bold",
                marginBottom: "20px",
                paddingBottom: "10px",
                borderBottom: `2px solid ${buttonColor}`,
              }}
            >
              SubCategory
            </h5>

            {/* Number of Columns */}
            <div className="row">
              <div className="col-md-12">
                <span style={{ fontWeight: "normal" }}>
                  Number of Button Columns:
                </span>
                <div className="setting form-group">
                  <select
                    value={subCategoryConfig.numColumns}
                    onChange={(e) =>
                      handleSubCategoryChange("numColumns", e.target.value)
                    }
                    className="form-control"
                  >
                    <option value={""}>Select Button Columns</option>
                    <option value={"center-single-row"}>
                      Center Single Row
                    </option>
                    <option value={"center-two-buttons-per-row"}>
                      Center 2 Buttons Per Row
                    </option>
                    <option value={"left-side-one-button-per-row"}>
                      Left Side One Button Per Row
                    </option>
                    <option value={"right-side-one-button-per-row"}>
                      Right Side One Button Per Row
                    </option>
                    <option value={"unlimited-buttons-center"}>
                      Unlimited Buttons Center
                    </option>
                    <option value={"unlimited-left-buttons-center"}>
                      Unlimited Left Buttons Center
                    </option>
                    <option value={"unlimited-right-buttons-center"}>
                      Unlimited Right Buttons Center
                    </option>
                  </select>
                </div>
              </div>
            </div>

            {/* Column Spacing */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Column Spacing In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={subCategoryConfig.columnSpacing}
                onChange={(e) =>
                  handleSubCategoryChange("columnSpacing", Number(e.target.value))
                }
              />
            </div>

            {/* Button Gap */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>Button Gap In px:</label>
              <input
                type="number"
                className="form-control"
                value={subCategoryConfig.buttonGap || 10}
                onChange={(e) =>
                  handleSubCategoryChange("buttonGap", Number(e.target.value))
                }
              />
            </div>

            {/* Button Margin Top */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Margin Top In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={subCategoryConfig.buttonMarginTop || 0}
                onChange={(e) =>
                  handleSubCategoryChange(
                    "buttonMarginTop",
                    Number(e.target.value),
                  )
                }
              />
            </div>

            {/* Button Margin Bottom */}
            <div className="setting form-group">
              <label style={{ fontWeight: "normal" }}>
                Button Margin Bottom In px:
              </label>
              <input
                type="number"
                className="form-control"
                value={subCategoryConfig.buttonMarginBottom || 0}
                onChange={(e) =>
                  handleSubCategoryChange(
                    "buttonMarginBottom",
                    Number(e.target.value),
                  )
                }
              />
            </div>

            {/* Disable Animation */}
            <div className="setting form-group custom-control custom-checkbox">
              <input
                type="checkbox"
                checked={subCategoryConfig.disableAnimation}
                onChange={(e) =>
                  handleSubCategoryChange("disableAnimation", e.target.checked)
                }
                id="disable_animation_sub"
                className="custom-control-input"
              />
              <label
                className="custom-control-label"
                htmlFor="disable_animation_sub"
              >
                Disable Button Animation
              </label>
            </div>

            {/* Enable Offset */}
            <div className="setting form-group custom-control custom-checkbox">
              <input
                type="checkbox"
                checked={subCategoryConfig.enableOffset}
                onChange={(e) =>
                  handleSubCategoryChange("enableOffset", e.target.checked)
                }
                id="enable_offset_sub"
                className="custom-control-input"
              />
              <label className="custom-control-label" htmlFor="enable_offset_sub">
                Enable Offset
              </label>
            </div>

            {subCategoryConfig.enableOffset && (
              <>
                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Left Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={subCategoryConfig.leftOffset}
                    onChange={(e) =>
                      handleSubCategoryChange("leftOffset", Number(e.target.value))
                    }
                  />
                </div>

                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Right Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={subCategoryConfig.rightOffset}
                    onChange={(e) =>
                      handleSubCategoryChange(
                        "rightOffset",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>

                <div className="setting form-group">
                  <label style={{ fontWeight: "normal" }}>
                    Enter Top Offset in %:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={subCategoryConfig.topOffset}
                    onChange={(e) =>
                      handleSubCategoryChange("topOffset", Number(e.target.value))
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Save and Cancel Buttons */}
        <div className="actions" style={{ marginTop: "30px" }}>
          <button
            type="button"
            onClick={handleSave}
            style={{ backgroundColor: buttonColor }}
          >
            Save All Settings
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{ backgroundColor: buttonColor }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategorySlidePanelForm;
